import { type LlmBlockDetail, getFileLlmBlockRaw } from "@/services/api";
import { llmReviewMessages, llmReviewReasonMessages } from "@/services/messages";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Loader2 } from "lucide-react";
import { useState } from "react";

interface BlockDetailExpandProps {
	fileId: number;
	block: LlmBlockDetail;
}

/** Структура `llm_review_json` блока (фича 027). */
interface LlmReviewJson {
	blockConfidence?: "high" | "low";
	works?: Array<{
		code?: string | null;
		workName?: string;
		unit?: string | null;
		quantity?: number;
		rate?: number;
		total?: number;
		materials?: unknown[];
		absorbedRows?: number[];
		workConfidence?: "high" | "low";
	}>;
	discardedRows?: number[];
	discardedExtras?: number[];
}

function safeParse(raw: string): LlmReviewJson | null {
	try {
		return JSON.parse(raw) as LlmReviewJson;
	} catch {
		return null;
	}
}

export function BlockDetailExpand({ fileId, block }: BlockDetailExpandProps): React.JSX.Element {
	const m = llmReviewMessages;

	const { data, isLoading, isError } = useQuery({
		queryKey: ["file-llm-block-raw", fileId, block.blockId],
		queryFn: () => getFileLlmBlockRaw(fileId, block.blockId),
		staleTime: 5 * 60 * 1000, // 5 минут — содержимое блока не меняется
		retry: 1,
	});

	const [rawOpen, setRawOpen] = useState(false);
	const [copied, setCopied] = useState(false);

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 px-1 py-2 text-sm text-slate-600">
				<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
				<span>{m.expand_loading_raw}</span>
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
				{m.expand_load_error}
			</div>
		);
	}

	const parsed = safeParse(data);
	const reasonText = block.llmReviewReason
		? (llmReviewReasonMessages[block.llmReviewReason] ?? block.llmReviewReason)
		: null;

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(data);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Fallback — старые браузеры: создаём textarea и копируем через execCommand.
			const ta = document.createElement("textarea");
			ta.value = data;
			document.body.appendChild(ta);
			ta.select();
			try {
				document.execCommand("copy");
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			} catch {
				/* совсем глухо */
			}
			document.body.removeChild(ta);
		}
	};

	return (
		<div className="space-y-4 text-sm">
			{/* Что LLM "увидел" + blockConfidence */}
			{parsed?.blockConfidence ? (
				<section>
					<h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
						{m.expand_what_llm_saw}
					</h4>
					<p className="mt-1 text-slate-700">
						{parsed.blockConfidence === "high"
							? m.expand_block_confidence_high
							: m.expand_block_confidence_low}
						{" — "}
						<span className="font-mono text-xs text-slate-500">
							blockConfidence = {parsed.blockConfidence}
						</span>
					</p>
				</section>
			) : null}

			{/* Why not applied (для skipped/failed) */}
			{block.llmReviewStatus !== "applied" && reasonText ? (
				<section>
					<h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
						{m.expand_why_not_applied}
					</h4>
					<p className="mt-1 text-slate-700">{reasonText}</p>
				</section>
			) : null}

			{/* Предложенные работы works[] */}
			<section>
				<h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
					{m.expand_proposed_works}
				</h4>
				{parsed?.works && parsed.works.length > 0 ? (
					<div className="mt-1 overflow-hidden rounded border border-slate-200">
						<table className="w-full text-xs">
							<thead className="bg-slate-100 text-left font-medium text-slate-600">
								<tr>
									<th className="px-2 py-1">{m.expand_work_field_code}</th>
									<th className="px-2 py-1">{m.expand_work_field_name}</th>
									<th className="px-2 py-1">{m.expand_work_field_unit}</th>
									<th className="px-2 py-1 text-right">{m.expand_work_field_quantity}</th>
									<th className="px-2 py-1 text-right">{m.expand_work_field_rate}</th>
									<th className="px-2 py-1 text-right">{m.expand_work_field_total}</th>
									<th className="px-2 py-1">{m.expand_absorbed_rows}</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-200 bg-white">
								{parsed.works.map((w, i) => {
									const absorbed = w.absorbedRows?.join(", ") ?? "—";
									const confidenceTag =
										w.workConfidence === "low" ? (
											<span className="ml-1 text-amber-700">({m.expand_work_confidence_low})</span>
										) : null;
									return (
										<tr key={`${w.code ?? "—"}-${i}`} className="text-slate-800">
											<td className="px-2 py-1 font-mono">{w.code ?? "—"}</td>
											<td className="px-2 py-1">
												{w.workName ?? "—"}
												{confidenceTag}
											</td>
											<td className="px-2 py-1">{w.unit ?? "—"}</td>
											<td className="px-2 py-1 text-right tabular-nums">{w.quantity ?? "—"}</td>
											<td className="px-2 py-1 text-right tabular-nums">{w.rate ?? "—"}</td>
											<td className="px-2 py-1 text-right tabular-nums">{w.total ?? "—"}</td>
											<td className="px-2 py-1 font-mono text-[11px] text-slate-500">{absorbed}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				) : (
					<p className="mt-1 text-slate-500">{m.expand_proposed_works_empty}</p>
				)}
			</section>

			{/* Discarded rows */}
			{(parsed?.discardedRows?.length ?? 0) > 0 || (parsed?.discardedExtras?.length ?? 0) > 0 ? (
				<section>
					<h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
						{m.expand_discarded_rows}
					</h4>
					<p className="mt-1 font-mono text-xs text-slate-600">
						{parsed?.discardedRows?.length
							? parsed.discardedRows.join(", ")
							: m.expand_discarded_rows_empty}
					</p>
					{parsed?.discardedExtras?.length ? (
						<>
							<h4 className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
								{m.expand_discarded_extras}
							</h4>
							<p className="mt-1 font-mono text-xs text-slate-600">
								{parsed.discardedExtras.join(", ")}
							</p>
						</>
					) : null}
				</section>
			) : null}

			{/* Сырой JSON */}
			<section>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => setRawOpen((s) => !s)}
						className="text-xs font-medium text-blue-600 hover:underline"
					>
						{rawOpen ? m.expand_hide_raw_button : m.expand_show_raw_button}
					</button>
					{rawOpen ? (
						<button
							type="button"
							onClick={handleCopy}
							className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
						>
							{copied ? (
								<>
									<Check className="h-3 w-3" aria-hidden="true" />
									{m.expand_copy_raw_done}
								</>
							) : (
								<>
									<Copy className="h-3 w-3" aria-hidden="true" />
									{m.expand_copy_raw}
								</>
							)}
						</button>
					) : null}
				</div>
				{rawOpen ? (
					<pre className="mt-2 max-h-96 overflow-auto rounded border border-slate-200 bg-slate-900 px-3 py-2 text-[11px] leading-relaxed text-slate-100">
						{data}
					</pre>
				) : null}
			</section>
		</div>
	);
}
